import { useMutation } from "@tanstack/react-query"
import urlBase64ToUint8Array from "./urlBase64ToUint8Array"
import useSnackbarStore from "stores/snackbar"
import useSessionStore from "stores/session"
import useGlobalStore from "stores/global"
import useT from "./useT"
import useRouterStore from "stores/routerStore"
import getLocationName from "./getLocationName"
import { checkAndUpdateIOSVersion } from "./getIOSVersion"
import { event } from "./gtag"

const useNotifications = () => {
  const t = useT()
  const uid = useSessionStore((state) => state.user.uid)
  const email = useSessionStore((state) => state.user.email)
  const locale = useRouterStore((state) => state.router?.locale)
  const toggleToast = useSnackbarStore((store) => store.toggleToast)
  const toggleSnackbar = useSnackbarStore((store) => store.toggleSnackbar)
  const setNotificationsStatus = useGlobalStore(
    (store) => store.setNotificationsStatus,
  )

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: ({
      subscription,
      location,
    }: {
      subscription: PushSubscription
      location: string
    }) =>
      fetch("/api/save-subscription", {
        method: "post",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          subscription,
          uid,
          email,
          locale,
          date: new Date().getTime(),
          location:
            process.env.NODE_ENV == "development" ? "development" : location,
        }),
      }),
    onSuccess(data: any) {
      console.log(data)
      setNotificationsStatus(true) // Update the subscription status
      event({
        action: "notifications_sub_success",
        category: "notifications",
        label: "subscribed to notifications successfully",
      })
    },
    onError(error) {
      console.log(error)
      toggleToast({
        color: "red",
        text: "Notification subscription failed to send data to save-subscription api",
      })
      setNotificationsStatus(false) // Update the subscription status
      event({
        action: "notifications_sub_failed_server",
        category: "notifications",
        label: "failed to subscribed to notifications",
        value: JSON.stringify(error),
      })
    },
  })

  const NEXT_PUBLIC_VAPID_PUBLIC_KEY: string =
    process.env.NEXT_PUBLIC_VAPID_PUBLIC_KEY!
  const subscribe = async () => {
    checkAndUpdateIOSVersion(t)
    const registration = await navigator.serviceWorker.getRegistration("/sw.js")
    const location = await getLocationName()
    // Triggers popup to request access to send notifications
    const result = await window.Notification.requestPermission()
    // If the user rejects the permission result will be "denied"
    if (registration?.active && result === "granted") {
      await registration?.showNotification(t.notifications.success, {
        tag: "https://www.appollow.net/?ref=Notification success",
        body: t.notifications.successMsg,
      } as NotificationOptions)
      const subscription = await registration?.pushManager.subscribe({
        applicationServerKey: urlBase64ToUint8Array(
          NEXT_PUBLIC_VAPID_PUBLIC_KEY,
        ),
        userVisibleOnly: true,
      })
      await mutateAsync({ subscription, location })
    } else if (result === "denied") {
      toggleSnackbar({
        color: "blue",
        text: t.notifications.enableDenied,
      })
      event({
        action: "notifications_sub_denied",
        category: "notifications",
        label: "denied to subscribed to notifications",
        value: JSON.stringify({ result, registration }),
      })
    } else {
      setNotificationsStatus(false) // Update the subscription status if permission is denied
      toggleSnackbar({
        color: "red",
        text: t.snackbar.error,
      })
      event({
        action: "notifications_sub_failed",
        category: "notifications",
        label: "failed to subscribed to notifications",
        value: JSON.stringify({ result, registration }),
      })
    }
  }

  return { subscribe, isLoading } // Return the subscription status
}

export default useNotifications
